import { useOptions } from '../hooks';
import { actions } from '../models';
import { useAppDispatch, useAppSelector } from '../store';
import type { NomineeTrackingParams, NomineeType, PollType } from '../types';
import { pushUserVote } from '../utils/apis';
import { showRegister } from '../utils/IdpUtils';
import LocalStorageClient from '../utils/LocalStorageClient';

/**
 * Custom hook for managing the state and behavior of the vote "like" button.
 *
 * @param {PollType} poll - The poll object.
 * @param {NomineeType} nominee - The nominee object.
 * @returns {Object} The state and behavior of the "like" button.
 */
export const useLikeButtonVote = (poll: PollType, nominee: NomineeType) => {
  const user = useAppSelector((state) => state.user);
  const sponsor = useAppSelector((state) => state.sponsors?.sponsor);
  const dispatch = useAppDispatch();
  const { enableLoginFlow, appType, promoTag, competitionBanner: competition } = useOptions();

  const vote = async (trackingFunction: (params: NomineeTrackingParams) => void, htmlEl?: HTMLElement) => {
    if (!user.isLogged && enableLoginFlow && !promoTag) {
      showRegister();
    }

    trackingFunction({ poll, nominee, appType, competition, sponsor, htmlEl });

    user && (await pushUserVote(poll, nominee, user));

    LocalStorageClient.trackVote(poll, nominee, user);
    dispatch(actions.user.setUserVoted(poll));

    if (promoTag) {
      dispatch(actions.prizeModal.setVisible(true));
    } else {
      dispatch(actions.loginModal.setVisible(true));
    }
  };

  return { vote };
};
